'use client';

import React, { useState, useEffect } from 'react';
import { X, Building2, Shield, FileText, Activity, AlertTriangle, FolderOpen, Clock, ChevronRight, ExternalLink, CheckCircle2, AlertCircle, User, Eye, GitBranch, Lock, Unlock, CheckSquare, Square, ArrowRight, ThumbsUp, ThumbsDown, RotateCcw, Send, ChevronDown, BarChart2 } from 'lucide-react';
import { type Vendor } from '@/lib/mockData';
import { ISSUES } from '@/lib/mockData';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';
import { workflowService, auditService, vendorService, type DbWorkflowApproval, type LifecycleStage } from '@/lib/supabase/tprmService';
import { toast } from 'sonner';


// ─── Lifecycle stages ────────────────────────────────────────────────────────
const LIFECYCLE_STAGES = [
  'Intake',
  'Sourcing',
  'Due Diligence',
  'Contracting',
  'Onboarding',
  'Active',
  'Renewal',
];

// ─── Stage-gate requirements (gates that must be checked before advancing) ───
type GateItem = { id: string; label: string; required: boolean };

const STAGE_GATES: Record<string, GateItem[]> = {
  Intake: [
    { id: 'g-intake-1', label: 'Vendor intake form submitted', required: true },
    { id: 'g-intake-2', label: 'Business justification documented', required: true },
    { id: 'g-intake-3', label: 'Preliminary risk tier assigned', required: true },
    { id: 'g-intake-4', label: 'Department owner identified', required: true },
  ],
  Sourcing: [
    { id: 'g-src-1', label: 'RFP / sole-source justification on file', required: true },
    { id: 'g-src-2', label: 'Vendor references checked', required: true },
    { id: 'g-src-3', label: 'Preliminary security questionnaire sent', required: true },
    { id: 'g-src-4', label: 'Procurement team notified', required: false },
  ],
  'Due Diligence': [
    { id: 'g-dd-1', label: 'Security questionnaire completed', required: true },
    { id: 'g-dd-2', label: 'Privacy impact assessment complete', required: true },
    { id: 'g-dd-3', label: 'Financial / background check passed', required: true },
    { id: 'g-dd-4', label: 'All critical findings remediated or accepted', required: true },
    { id: 'g-dd-5', label: 'DD review approved by Risk Officer', required: true },
  ],
  Contracting: [
    { id: 'g-con-1', label: 'Contract draft reviewed by Legal', required: true },
    { id: 'g-con-2', label: 'Data Processing Agreement (DPA) executed', required: true },
    { id: 'g-con-3', label: 'Insurance certificates received & verified', required: true },
    { id: 'g-con-4', label: 'Contract signed by both parties', required: true },
    { id: 'g-con-5', label: 'City Manager / Council approval (if required)', required: true },
  ],
  Onboarding: [
    { id: 'g-onb-1', label: 'System access provisioned per least-privilege', required: true },
    { id: 'g-onb-2', label: 'Vendor orientation / security briefing completed', required: true },
    { id: 'g-onb-3', label: 'Monitoring schedule established', required: true },
    { id: 'g-onb-4', label: 'Initial KPIs / SLAs baselined', required: false },
  ],
  Active: [
    { id: 'g-act-1', label: 'Renewal notice period triggered (≥90 days)', required: true },
    { id: 'g-act-2', label: 'Annual performance review completed', required: true },
    { id: 'g-act-3', label: 'No open critical issues', required: true },
    { id: 'g-act-4', label: 'Renewal risk re-assessment initiated', required: true },
  ],
  Renewal: [
    { id: 'g-ren-1', label: 'Renewal terms negotiated', required: true },
    { id: 'g-ren-2', label: 'Updated DD / security review complete', required: true },
    { id: 'g-ren-3', label: 'Renewed contract executed', required: true },
  ],
};

// ─── Approval roles per stage ─────────────────────────────────────────────────
const STAGE_APPROVERS: Record<string, string[]> = {
  Intake:         ['Department Owner', 'Procurement Officer'],
  Sourcing:       ['Procurement Officer', 'Risk Analyst'],
  'Due Diligence':['Risk Officer', 'CISO'],
  Contracting:    ['City Attorney', 'Procurement Officer', 'City Manager'],
  Onboarding:     ['IT Security', 'Department Owner'],
  Active:         ['Contract Owner', 'Risk Officer'],
  Renewal:        ['City Manager', 'Procurement Officer'],
};

// ─── Simulated approval history per stage ────────────────────────────────────
type ApprovalRecord = {
  stage: string;
  approver: string;
  action: 'Approved' | 'Rejected' | 'Returned';
  timestamp: string;
  comment: string;
};

const MOCK_APPROVAL_HISTORY: ApprovalRecord[] = [
  { stage: 'Intake',         approver: 'Diane Harrington', action: 'Approved',  timestamp: '2024-01-10 09:15', comment: 'Business case approved. Proceed to sourcing.' },
  { stage: 'Sourcing',       approver: 'Marcus Webb',       action: 'Approved',  timestamp: '2024-02-05 14:30', comment: 'Sole-source justification accepted.' },
  { stage: 'Due Diligence',  approver: 'Kevin Tanaka',      action: 'Returned',  timestamp: '2024-03-01 11:00', comment: 'MFA gap must be addressed before approval.' },
  { stage: 'Due Diligence',  approver: 'Kevin Tanaka',      action: 'Approved',  timestamp: '2024-03-15 16:00', comment: 'MFA remediation confirmed. DD approved.' },
  { stage: 'Contracting',    approver: 'Sandra Chu',        action: 'Approved',  timestamp: '2024-04-02 10:45', comment: 'Contract and DPA executed. Insurance on file.' },
  { stage: 'Onboarding',     approver: 'Kevin Tanaka',      action: 'Approved',  timestamp: '2024-05-01 09:00', comment: 'Access provisioned. Security briefing complete.' },
];

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'tab-summary',    label: 'Summary',    icon: Building2 },
  { key: 'tab-risk',       label: 'Risk',       icon: Shield },
  { key: 'tab-contract',   label: 'Contract',   icon: FileText },
  { key: 'tab-monitoring', label: 'Monitoring', icon: Activity },
  { key: 'tab-issues',     label: 'Issues',     icon: AlertTriangle },
  { key: 'tab-workflow',   label: 'Workflow',   icon: GitBranch },
  { key: 'tab-docs',       label: 'Documents',  icon: FolderOpen },
  { key: 'tab-audit',      label: 'Audit Trail',icon: Clock },
];

interface Props {
  vendor: Vendor;
  onClose: () => void;
}

function getStageIndex(stage: string): number {
  return LIFECYCLE_STAGES.indexOf(stage);
}

// ─── Completeness gauge helpers ───────────────────────────────────────────────

type CompletenessCategory = {
  label: string;
  collected: number;
  required: number;
  color: string;
};

function getCompletenessCategories(vendor: Vendor, approvalHistory: ApprovalRecord[]): CompletenessCategory[] {
  // Documents: based on ddStatus and contractStatus
  const docsCollected =
    (vendor.ddStatus === 'Completed' || vendor.ddStatus === 'Approved' ? 2 : vendor.ddStatus === 'In Progress' ? 1 : 0) +
    (vendor.contractStatus === 'Active' || vendor.contractStatus === 'Executed' ? 1 : 0);
  const docsRequired = 3;

  // Insurance: based on insuranceStatus
  const insuranceCollected =
    vendor.insuranceStatus === 'Verified' ? 2 : vendor.insuranceStatus === 'Pending' ? 1 : 0;
  const insuranceRequired = 2;

  // Assessments: based on riskTier and ddStatus
  const assessmentsCollected =
    (vendor.riskTier !== 'Unknown' ? 1 : 0) +
    (vendor.ddStatus === 'Completed' || vendor.ddStatus === 'Approved' ? 1 : 0) +
    (vendor.residualRisk !== 'Unknown' && vendor.residualRisk ? 1 : 0);
  const assessmentsRequired = 3;

  // Approvals: based on approval history stages completed
  const approvedStages = new Set(
    approvalHistory.filter((a) => a.action === 'Approved').map((a) => a.stage)
  );
  const approvalsCollected = Math.min(approvedStages.size, 4);
  const approvalsRequired = 4;

  return [
    { label: 'Documents', collected: docsCollected, required: docsRequired, color: '#0284c7' },
    { label: 'Insurance', collected: insuranceCollected, required: insuranceRequired, color: '#0d9488' },
    { label: 'Assessments', collected: assessmentsCollected, required: assessmentsRequired, color: '#7c3aed' },
    { label: 'Approvals', collected: approvalsCollected, required: approvalsRequired, color: '#d97706' },
  ];
}

function CompletenessGauge({ vendor, approvalHistory }: { vendor: Vendor; approvalHistory: ApprovalRecord[] }) {
  const categories = getCompletenessCategories(vendor, approvalHistory);
  const totalCollected = categories.reduce((s, c) => s + c.collected, 0);
  const totalRequired = categories.reduce((s, c) => s + c.required, 0);
  const overallPct = totalRequired > 0 ? Math.round((totalCollected / totalRequired) * 100) : 0;

  const gaugeColor =
    overallPct >= 80 ? '#0d9488' : overallPct >= 50 ? '#d97706' : '#dc2626';

  // SVG arc gauge (semicircle)
  const radius = 40;
  const circumference = Math.PI * radius; // half circle
  const offset = circumference - (overallPct / 100) * circumference;

  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <BarChart2 size={13} className="text-[hsl(var(--text-muted))]" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
          Vendor Completeness
        </h3>
      </div>

      {/* Semicircle gauge */}
      <div className="flex items-end justify-center gap-6">
        <div className="relative flex flex-col items-center">
          <svg width="100" height="56" viewBox="0 0 100 56">
            {/* Background arc */}
            <path
              d="M 10 50 A 40 40 0 0 1 90 50"
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth="10"
              strokeLinecap="round"
            />
            {/* Foreground arc */}
            <path
              d="M 10 50 A 40 40 0 0 1 90 50"
              fill="none"
              stroke={gaugeColor}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={`${offset}`}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <div className="absolute bottom-0 flex flex-col items-center">
            <span className="text-xl font-bold leading-none" style={{ color: gaugeColor }}>
              {overallPct}%
            </span>
            <span className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">complete</span>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="flex-1 space-y-2 min-w-0">
          {categories.map((cat) => {
            const pct = cat.required > 0 ? Math.round((cat.collected / cat.required) * 100) : 0;
            return (
              <div key={cat.label} className="space-y-0.5">
                <div className="flex items-center justify-between">
                  <span className="text-2xs text-[hsl(var(--text-muted))]">{cat.label}</span>
                  <span className="text-2xs font-semibold tabular-nums" style={{ color: cat.color }}>
                    {cat.collected}/{cat.required}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: cat.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function VendorDetailModal({ vendor, onClose }: Props) {
  const [activeTab, setActiveTab] = useState('tab-summary');
  const [currentStage, setCurrentStage] = useState(vendor.lifecycleStage as string);
  const [checkedGates, setCheckedGates] = useState<Record<string, boolean>>({});
  const [approvalComment, setApprovalComment] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<ApprovalRecord[]>(MOCK_APPROVAL_HISTORY);
  const [showHistory, setShowHistory] = useState(false);
  const [dbApprovals, setDbApprovals] = useState<DbWorkflowApproval[]>([]);

  const vendorIssues = ISSUES.filter((i) => i.vendorId === vendor.id);

  // Load workflow approvals from Supabase
  useEffect(() => {
    workflowService.getByVendorId(vendor.id).then((approvals) => {
      if (approvals.length > 0) {
        setDbApprovals(approvals);
        // Map DB approvals to local ApprovalRecord format
        const mapped: ApprovalRecord[] = approvals.map((a) => ({
          stage: a.stage,
          approver: a.approver,
          action: a.action as 'Approved' | 'Rejected' | 'Returned',
          timestamp: new Date(a.created_at).toISOString().replace('T', ' ').slice(0, 16),
          comment: a.comment,
        }));
        setApprovalHistory(mapped);
      }
    }).catch(() => {
      // Fall back to mock data if DB not available
    });
  }, [vendor.id]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const stageIdx = getStageIndex(currentStage);
  const nextStage = LIFECYCLE_STAGES[stageIdx + 1] ?? null;
  const prevStage = LIFECYCLE_STAGES[stageIdx - 1] ?? null;

  // Gates for the CURRENT stage (must be satisfied before advancing)
  const currentGates = STAGE_GATES[currentStage] ?? [];
  const requiredGates = currentGates.filter((g) => g.required);
  const allRequiredChecked = requiredGates.every((g) => checkedGates[g.id]);
  const checkedCount = currentGates.filter((g) => checkedGates[g.id]).length;
  const completionPct = currentGates.length > 0 ? Math.round((checkedCount / currentGates.length) * 100) : 100;

  // Approvers for the next stage transition
  const nextApprovers = nextStage ? (STAGE_APPROVERS[currentStage] ?? []) : [];

  function toggleGate(id: string) {
    setCheckedGates((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function handleAdvanceStage() {
    if (!allRequiredChecked) {
      toast.error('All required gate items must be checked before advancing.');
      return;
    }
    if (!nextStage) {
      toast.info('Vendor is already at the final stage.');
      return;
    }
    setShowApproveConfirm(true);
  }

  async function confirmAdvance() {
    if (!nextStage) return;
    const record: ApprovalRecord = {
      stage: currentStage,
      approver: 'Current User',
      action: 'Approved',
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
      comment: approvalComment || `Stage gate approved. Advancing to ${nextStage}.`,
    };
    setApprovalHistory((prev) => [...prev, record]);
    setCurrentStage(nextStage);
    setCheckedGates({});
    setApprovalComment('');
    setShowApproveConfirm(false);
    toast.success(`Vendor advanced to ${nextStage} stage.`, {
      description: `All gate requirements satisfied. Approval recorded.`,
    });

    // Persist to Supabase
    try {
      await workflowService.addApproval({
        vendor_id: vendor.id,
        stage: currentStage,
        approver: 'Current User',
        action: 'Approved',
        comment: record.comment,
      });
      await vendorService.updateLifecycleStage(vendor.id, nextStage as LifecycleStage);
      await auditService.log({
        entity_type: 'Vendor',
        entity_id: vendor.vendorId,
        action: 'Stage Advanced',
        actor: 'Current User',
        actor_role: 'Procurement',
        detail: `Vendor advanced from ${currentStage} to ${nextStage}. ${record.comment}`,
      });
    } catch {
      // Non-blocking — UI already updated
    }
  }

  function handleReject() {
    setShowRejectConfirm(true);
  }

  async function confirmReject() {
    const record: ApprovalRecord = {
      stage: currentStage,
      approver: 'Current User',
      action: 'Rejected',
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
      comment: approvalComment || 'Stage advancement rejected.',
    };
    setApprovalHistory((prev) => [...prev, record]);
    setApprovalComment('');
    setShowRejectConfirm(false);
    toast.error('Stage advancement rejected.', {
      description: 'Rejection recorded in audit trail.',
    });

    try {
      await workflowService.addApproval({
        vendor_id: vendor.id,
        stage: currentStage,
        approver: 'Current User',
        action: 'Rejected',
        comment: record.comment,
      });
      await auditService.log({
        entity_type: 'Vendor',
        entity_id: vendor.vendorId,
        action: 'Stage Rejected',
        actor: 'Current User',
        actor_role: 'Procurement',
        detail: `Stage advancement from ${currentStage} rejected. ${record.comment}`,
      });
    } catch {
      // Non-blocking
    }
  }

  async function handleReturnToPrev() {
    if (!prevStage) {
      toast.info('Already at the first stage.');
      return;
    }
    const record: ApprovalRecord = {
      stage: currentStage,
      approver: 'Current User',
      action: 'Returned',
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
      comment: approvalComment || `Returned to ${prevStage} for remediation.`,
    };
    setApprovalHistory((prev) => [...prev, record]);
    setCurrentStage(prevStage);
    setCheckedGates({});
    setApprovalComment('');
    toast.warning(`Vendor returned to ${prevStage} stage.`, {
      description: 'Return recorded in audit trail.',
    });

    try {
      await workflowService.addApproval({
        vendor_id: vendor.id,
        stage: currentStage,
        approver: 'Current User',
        action: 'Returned',
        comment: record.comment,
      });
      await vendorService.updateLifecycleStage(vendor.id, prevStage as LifecycleStage);
      await auditService.log({
        entity_type: 'Vendor',
        entity_id: vendor.vendorId,
        action: 'Stage Returned',
        actor: 'Current User',
        actor_role: 'Procurement',
        detail: `Vendor returned from ${currentStage} to ${prevStage}. ${record.comment}`,
      });
    } catch {
      // Non-blocking
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col card shadow-md animate-slide-up overflow-hidden">
        {/* Modal header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-bold text-[hsl(var(--text-primary))]">{vendor.name}</h2>
              {vendor.dba && (
                <span className="text-xs text-[hsl(var(--text-muted))]">dba {vendor.dba}</span>
              )}
              <StatusBadge type="overall" value={vendor.overallStatus} />
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs font-mono text-[hsl(var(--text-muted))]">{vendor.vendorId}</span>
              <span className="text-xs text-[hsl(var(--text-muted))]">{vendor.category}</span>
              <span className="text-xs text-[hsl(var(--text-muted))]">{vendor.department}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              href={`/vendor-workspace/${vendor.id}`}
              className="btn-secondary text-xs"
            >
              <ExternalLink size={12} />
              Open Full Record
            </Link>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Lifecycle progress rail */}
        <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] flex-shrink-0">
          <div className="flex items-center gap-0">
            {LIFECYCLE_STAGES.map((stage, i) => {
              const isComplete = i < stageIdx;
              const isCurrent = i === stageIdx;
              return (
                <React.Fragment key={`ls-${stage}`}>
                  <div className="flex flex-col items-center gap-1 min-w-0">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-2xs font-bold flex-shrink-0 transition-all ${
                        isComplete
                          ? 'bg-teal-600 text-white'
                          : isCurrent
                          ? 'bg-teal-100 border-2 border-teal-600 text-teal-700 dark:bg-teal-950' :'bg-[hsl(var(--border))] text-[hsl(var(--text-muted))]'
                      }`}
                    >
                      {isComplete ? '✓' : i + 1}
                    </div>
                    <span
                      className={`text-2xs whitespace-nowrap ${
                        isCurrent
                          ? 'font-semibold text-teal-700 dark:text-teal-400'
                          : isComplete
                          ? 'text-[hsl(var(--text-secondary))]'
                          : 'text-[hsl(var(--text-muted))]'
                      }`}
                    >
                      {stage}
                    </span>
                  </div>
                  {i < LIFECYCLE_STAGES.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-1 mb-4 ${
                        i < stageIdx ? 'bg-teal-500' : 'bg-[hsl(var(--border))]'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[hsl(var(--border))] flex-shrink-0 overflow-x-auto bg-[hsl(var(--surface))]">
          {TABS.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.key;
            const isBadged = tab.key === 'tab-issues' && vendorIssues.length > 0;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                  isActive
                    ? 'border-teal-600 text-teal-700 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-950/20' :'border-transparent text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--background))]'
                }`}
              >
                <TabIcon size={13} />
                {tab.label}
                {isBadged && (
                  <span className="ml-1 text-2xs bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400 px-1.5 py-0.5 rounded-full font-semibold tabular-nums">
                    {vendorIssues.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {/* ── Summary tab ─────────────────────────────────────────────────── */}
          {activeTab === 'tab-summary' && (
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Profile */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                  Vendor Profile
                </h3>
                <div className="space-y-2">
                  {[
                    { label: 'Legal Name', value: vendor.name },
                    { label: 'DBA', value: vendor.dba || '—' },
                    { label: 'Vendor ID', value: vendor.vendorId, mono: true },
                    { label: 'Category', value: vendor.category },
                    { label: 'Services', value: vendor.services },
                    { label: 'Hosting Model', value: vendor.hostingModel },
                    { label: 'Department', value: vendor.department },
                    { label: 'Dept. Owner', value: vendor.departmentOwner },
                    { label: 'Contract Owner', value: vendor.contractOwner },
                  ].map((row) => (
                    <div
                      key={`prof-${row.label}`}
                      className="flex items-start justify-between gap-2 py-1 border-b border-[hsl(var(--border))] last:border-0"
                    >
                      <span className="text-xs text-[hsl(var(--text-muted))] flex-shrink-0 w-28">
                        {row.label}
                      </span>
                      <span
                        className={`text-xs text-[hsl(var(--text-primary))] text-right ${
                          row.mono ? 'font-mono' : ''
                        }`}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk & Status */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                    Risk & Compliance Status
                  </h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Risk Tier',       node: <StatusBadge type="risk"     value={vendor.riskTier} /> },
                      { label: 'Criticality',     node: <StatusBadge type="risk"     value={vendor.criticality} /> },
                      { label: 'Residual Risk',   node: <StatusBadge type="risk"     value={vendor.residualRisk} /> },
                      { label: 'Contract Status', node: <StatusBadge type="contract" value={vendor.contractStatus} /> },
                      { label: 'DD Status',       node: <StatusBadge type="dd"       value={vendor.ddStatus} /> },
                      { label: 'Insurance',       node: <StatusBadge type="insurance" value={vendor.insuranceStatus} /> },
                      { label: 'Overall Status',  node: <StatusBadge type="overall"  value={vendor.overallStatus} /> },
                    ].map((row) => (
                      <div
                        key={`rs-${row.label}`}
                        className="flex items-center justify-between py-1 border-b border-[hsl(var(--border))] last:border-0"
                      >
                        <span className="text-xs text-[hsl(var(--text-muted))]">{row.label}</span>
                        {row.node}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Completeness Gauge */}
                <CompletenessGauge vendor={vendor} approvalHistory={approvalHistory} />

                {vendor.dataHandled.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                      Data Handled
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {vendor.dataHandled.map((d) => (
                        <span
                          key={`dh-${d}`}
                          className="badge bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {vendor.systemsAccessed.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                      Systems Accessed
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {vendor.systemsAccessed.map((s) => (
                        <span
                          key={`sa-${s}`}
                          className="badge bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Workflow tab ─────────────────────────────────────────────────── */}
          {activeTab === 'tab-workflow' && (
            <div className="p-5 space-y-5">

              {/* Current stage banner */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800">
                <div className="flex items-center gap-2">
                  <GitBranch size={15} className="text-teal-600" />
                  <div>
                    <p className="text-xs font-semibold text-teal-800 dark:text-teal-300">
                      Current Stage: {currentStage}
                    </p>
                    <p className="text-2xs text-teal-600 dark:text-teal-400 mt-0.5">
                      {nextStage
                        ? `Next stage: ${nextStage} — complete all required gates below to advance`
                        : 'Vendor has completed the full lifecycle'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-xs font-bold text-teal-700 dark:text-teal-300">{completionPct}%</p>
                    <p className="text-2xs text-teal-600">gates complete</p>
                  </div>
                  <div className="w-12 h-12 relative flex-shrink-0">
                    <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeWidth="3" className="text-teal-200 dark:text-teal-800" />
                      <circle
                        cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeWidth="3"
                        strokeDasharray={`${completionPct} ${100 - completionPct}`}
                        strokeLinecap="round"
                        className="text-teal-500"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Gate checklist */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                    Stage Gate Checklist — {currentStage}
                  </h3>
                  <span className="text-2xs text-[hsl(var(--text-muted))]">
                    {checkedCount} / {currentGates.length} items
                  </span>
                </div>
                <div className="space-y-1.5 border border-[hsl(var(--border))] rounded-lg overflow-hidden">
                  {currentGates.map((gate, idx) => {
                    const checked = !!checkedGates[gate.id];
                    return (
                      <button
                        key={gate.id}
                        onClick={() => toggleGate(gate.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          idx < currentGates.length - 1 ? 'border-b border-[hsl(var(--border))]' : ''
                        } ${checked ? 'bg-emerald-50/60 dark:bg-emerald-950/20' : 'hover:bg-[hsl(var(--background))]'}`}
                      >
                        {checked ? (
                          <CheckSquare size={14} className="text-emerald-600 flex-shrink-0" />
                        ) : (
                          <Square size={14} className="text-[hsl(var(--text-muted))] flex-shrink-0" />
                        )}
                        <span className={`text-xs flex-1 ${checked ? 'line-through text-[hsl(var(--text-muted))]' : 'text-[hsl(var(--text-primary))]'}`}>
                          {gate.label}
                        </span>
                        {gate.required ? (
                          <span className="text-2xs font-medium text-red-500 flex-shrink-0">Required</span>
                        ) : (
                          <span className="text-2xs text-[hsl(var(--text-muted))] flex-shrink-0">Optional</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Required approvers */}
              {nextApprovers.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                    Required Approvers for Advancement
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {nextApprovers.map((approver) => (
                      <div key={approver} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                        <User size={11} className="text-[hsl(var(--text-muted))]" />
                        <span className="text-xs text-[hsl(var(--text-secondary))]">{approver}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval comment */}
              <div>
                <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5">
                  Approval Comment / Notes
                </label>
                <textarea
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder="Add a comment for the audit trail (optional)…"
                  rows={2}
                  className="w-full text-xs px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-muted))] focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
                />
              </div>

              {/* Gate status notice */}
              {!allRequiredChecked && nextStage && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                  <Lock size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-semibold">Stage gate locked.</span>{' '}
                    Complete all required checklist items to unlock advancement to{' '}
                    <span className="font-semibold">{nextStage}</span>.
                  </p>
                </div>
              )}
              {allRequiredChecked && nextStage && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                  <Unlock size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">
                    <span className="font-semibold">Stage gate satisfied.</span>{' '}
                    All required items are complete. You may advance this vendor to{' '}
                    <span className="font-semibold">{nextStage}</span>.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {nextStage && (
                  <button
                    onClick={handleAdvanceStage}
                    disabled={!allRequiredChecked}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                      allRequiredChecked
                        ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-sm'
                        : 'bg-[hsl(var(--border))] text-[hsl(var(--text-muted))] cursor-not-allowed'
                    }`}
                  >
                    <ThumbsUp size={13} />
                    Approve & Advance to {nextStage}
                    <ArrowRight size={12} />
                  </button>
                )}
                <button
                  onClick={handleReject}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                >
                  <ThumbsDown size={13} />
                  Reject Advancement
                </button>
                {prevStage && (
                  <button
                    onClick={() => toast.info('Escalation notification sent to Risk Officer.')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border border-amber-300 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors ml-auto"
                  >
                    <RotateCcw size={13} />
                    Return to {prevStage}
                  </button>
                )}
                <button
                  onClick={() => toast.info('Escalation notification sent to Risk Officer.')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border border-amber-300 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors ml-auto"
                >
                  <Send size={13} />
                  Escalate
                </button>
              </div>

              {/* Approval history */}
              <div>
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] transition-colors"
                >
                  <Clock size={13} />
                  Approval History ({approvalHistory.length})
                  <ChevronDown size={13} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                </button>
                {showHistory && (
                  <div className="mt-2 border border-[hsl(var(--border))] rounded-lg overflow-hidden">
                    {approvalHistory.map((rec, i) => (
                      <div
                        key={`apr-${i}`}
                        className="flex items-start gap-3 px-4 py-3 text-xs border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--background))] transition-colors"
                      >
                        <div className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${
                          rec.action === 'Approved' ? 'bg-emerald-500' :
                          rec.action === 'Rejected' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="font-medium text-[hsl(var(--text-primary))]">{rec.approver}</span>
                            <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                              rec.action === 'Approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
                              rec.action === 'Rejected'? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                            }`}>
                              {rec.action}
                            </span>
                            <span className="text-2xs text-[hsl(var(--text-muted))] ml-auto font-mono">{rec.timestamp}</span>
                          </div>
                          <p className="text-2xs text-[hsl(var(--text-muted))] leading-snug">
                            Stage: <span className="font-medium text-[hsl(var(--text-secondary))]">{rec.stage}</span>
                            {' · '}{rec.comment}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Contract tab ─────────────────────────────────────────────────── */}
          {activeTab === 'tab-contract' && (
            <div className="p-5 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                Contract Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Contract Status', value: vendor.contractStatus, badge: true, type: 'contract' as const },
                  { label: 'Term Start',       value: vendor.contractStart || '—' },
                  { label: 'Term End',         value: vendor.contractEnd || '—' },
                  { label: 'Renewal Notice',   value: '90 days' },
                  { label: 'Contract Owner',   value: vendor.contractOwner },
                  { label: 'Last Review',      value: vendor.lastReview || '—' },
                  { label: 'Next Review',      value: vendor.nextReview || '—' },
                ].map((row) => (
                  <div
                    key={`ct-${row.label}`}
                    className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]"
                  >
                    <span className="text-xs text-[hsl(var(--text-muted))]">{row.label}</span>
                    {row.badge ? (
                      <StatusBadge type={row.type} value={row.value as string} />
                    ) : (
                      <span className="text-xs font-medium text-[hsl(var(--text-primary))] font-mono tabular-nums">
                        {row.value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                <p className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Scope of Services</p>
                <p className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">
                  {vendor.services}. Contract includes standard city security addendum, data processing
                  agreement, insurance requirements per city risk management schedule, and right-to-audit clause.
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => toast.info('Contract viewer coming soon.')} className="btn-secondary text-xs">
                  <FileText size={12} />
                  View Contract
                </button>
                <button onClick={() => toast.info('Amendment workflow coming soon.')} className="btn-ghost text-xs">
                  Request Amendment
                </button>
              </div>
            </div>
          )}

          {/* ── Issues tab ───────────────────────────────────────────────────── */}
          {activeTab === 'tab-issues' && (
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                  Open Issues ({vendorIssues.length})
                </h3>
                <button onClick={() => toast.info('New issue form coming soon.')} className="btn-primary text-xs">
                  + New Issue
                </button>
              </div>
              {vendorIssues.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2 opacity-60" />
                  <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">No open issues</p>
                  <p className="text-xs text-[hsl(var(--text-muted))] mt-1">This vendor has no active issues or findings.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {vendorIssues.map((issue) => (
                    <div key={issue.id} className="p-3 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--background))] transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-2xs font-mono text-[hsl(var(--text-muted))]">{issue.id}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <StatusBadge type="severity" value={issue.severity} />
                          <StatusBadge type="issue-status" value={issue.status} />
                        </div>
                      </div>
                      <p className="text-xs font-medium text-[hsl(var(--text-primary))] leading-snug mb-1">{issue.description}</p>
                      <div className="flex items-center gap-3 text-2xs text-[hsl(var(--text-muted))]">
                        <span>Category: {issue.category}</span>
                        <span>Owner: {issue.assignedOwner}</span>
                        <span>Due: {issue.dueDate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Monitoring tab ───────────────────────────────────────────────── */}
          {activeTab === 'tab-monitoring' && (
            <div className="p-5 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                Monitoring Schedule
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Monitoring Frequency', value: vendor.riskTier === 'Critical' ? 'Quarterly' : vendor.riskTier === 'High' ? 'Semi-Annual' : 'Annual' },
                  { label: 'Reassessment Cycle',   value: vendor.riskTier === 'Critical' || vendor.riskTier === 'High' ? 'Annual' : 'Renewal-triggered' },
                  { label: 'Last Review Date',     value: vendor.lastReview || 'Not yet reviewed' },
                  { label: 'Next Review Due',      value: vendor.nextReview || '—' },
                  { label: 'Open Issues',          value: String(vendor.openIssues) },
                  { label: 'Critical Issues',      value: String(vendor.openCriticalIssues) },
                ].map((row) => (
                  <div key={`mon-${row.label}`} className="p-3 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                    <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">{row.label}</p>
                    <p className="text-sm font-semibold text-[hsl(var(--text-primary))]">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Link href="/due-diligence-workspace" className="btn-primary text-xs">
                  <Activity size={12} />
                  View DD Workspace
                </Link>
                <button onClick={() => toast.info('Scheduling reassessment…')} className="btn-secondary text-xs">
                  Schedule Reassessment
                </button>
              </div>
            </div>
          )}

          {/* ── Risk tab ─────────────────────────────────────────────────────── */}
          {activeTab === 'tab-risk' && (
            <div className="p-5 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                Risk Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: 'Inherent Risk', value: vendor.riskTier,    type: 'risk' as const },
                  { label: 'Residual Risk', value: vendor.residualRisk, type: 'risk' as const },
                  { label: 'Criticality',   value: vendor.criticality,  type: 'risk' as const },
                ].map((row) => (
                  <div key={`rp-${row.label}`} className="p-3 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-center">
                    <p className="text-2xs text-[hsl(var(--text-muted))] mb-2">{row.label}</p>
                    <StatusBadge type={row.type} value={row.value} />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-[hsl(var(--text-secondary))]">Risk Factors</h4>
                {[
                  { factor: 'Nonpublic data access',              present: vendor.dataHandled.some(d => d.includes('PII') || d.includes('Payment') || d.includes('Records')) },
                  { factor: 'Payment data processing',            present: vendor.dataHandled.some(d => d.includes('Payment')) },
                  { factor: 'City network / system access',       present: vendor.systemsAccessed.length > 0 },
                  { factor: 'Cloud / SaaS hosted',                present: vendor.hostingModel === 'SaaS' || vendor.hostingModel === 'Managed Service' },
                  { factor: 'Essential public services dependency',present: vendor.criticality === 'Critical' },
                  { factor: 'Subcontractor dependency',           present: vendor.hostingModel !== 'Commodity' },
                ].map((rf) => (
                  <div key={`rf-${rf.factor}`} className="flex items-center gap-2 text-xs py-1 border-b border-[hsl(var(--border))] last:border-0">
                    {rf.present ? (
                      <AlertCircle size={13} className="text-amber-500 flex-shrink-0" />
                    ) : (
                      <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                    )}
                    <span className={rf.present ? 'text-[hsl(var(--text-primary))]' : 'text-[hsl(var(--text-muted))]'}>
                      {rf.factor}
                    </span>
                    <span className={`ml-auto text-2xs font-medium ${rf.present ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {rf.present ? 'Present' : 'Not applicable'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Audit Trail tab ──────────────────────────────────────────────── */}
          {activeTab === 'tab-audit' && (
            <div className="p-5 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                Audit Trail
              </h3>
              <div className="space-y-0 border border-[hsl(var(--border))] rounded-md overflow-hidden">
                {[
                  { ts: '2026-03-28 16:42', user: 'Kevin Tanaka',    action: 'Finding added to DD review',       detail: 'MFA gap identified in cybersecurity assessment.' },
                  { ts: '2026-03-15 11:00', user: 'Sandra Chu',      action: 'Contract review initiated',        detail: 'Renewal review initiated for upcoming contract expiry.' },
                  { ts: '2026-02-20 09:15', user: 'Patricia Nguyen', action: 'Issue opened',                     detail: 'Data retention non-conformance identified during privacy review.' },
                  { ts: '2026-02-14 14:30', user: 'Kevin Tanaka',    action: 'Issue opened',                     detail: 'MFA gap on admin accounts — critical severity assigned.' },
                  { ts: '2025-10-05 10:00', user: 'Marcus Webb',     action: 'Annual reassessment scheduled',    detail: 'Next full reassessment due 2026-04-30.' },
                  { ts: '2024-10-05 09:00', user: 'Marcus Webb',     action: 'Vendor record updated',            detail: 'Contract renewed. Insurance certificates received.' },
                ].map((entry, i) => (
                  <div
                    key={`at-${i}`}
                    className="flex items-start gap-3 px-4 py-3 text-xs border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--background))] transition-colors"
                  >
                    <span className="font-mono text-2xs text-[hsl(var(--text-muted))] flex-shrink-0 mt-0.5 w-32">
                      {entry.ts}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <User size={11} className="text-[hsl(var(--text-muted))]" />
                        <span className="font-medium text-[hsl(var(--text-primary))]">{entry.user}</span>
                        <ChevronRight size={11} className="text-[hsl(var(--text-muted))]" />
                        <span className="text-[hsl(var(--text-secondary))]">{entry.action}</span>
                      </div>
                      <p className="text-2xs text-[hsl(var(--text-muted))] leading-snug">{entry.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Documents tab ────────────────────────────────────────────────── */}
          {activeTab === 'tab-docs' && (
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
                  Documents
                </h3>
                <button onClick={() => toast.info('Document upload coming soon.')} className="btn-primary text-xs">
                  + Upload
                </button>
              </div>
              <div className="space-y-1.5">
                {[
                  { name: 'Executed Contract Agreement',    type: 'Contract',      date: vendor.contractStart || '—', expires: vendor.contractEnd || '—', sensitivity: 'Confidential' },
                  { name: 'Certificate of Insurance',       type: 'Insurance',     date: '2025-07-01',                expires: '2026-06-30',               sensitivity: 'Internal' },
                  { name: 'Security Questionnaire Response',type: 'Due Diligence', date: '2025-04-10',                expires: '2026-04-10',               sensitivity: 'Confidential' },
                  { name: 'Data Processing Agreement',      type: 'Contract',      date: vendor.contractStart || '—', expires: '—',                        sensitivity: 'Confidential' },
                  { name: 'W-9 / Vendor Setup Form',        type: 'Procurement',   date: vendor.contractStart || '—', expires: '—',                        sensitivity: 'Internal' },
                ].map((doc, i) => (
                  <div
                    key={`doc-${i}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--background))] transition-colors"
                  >
                    <FileText size={14} className="text-[hsl(var(--text-muted))] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[hsl(var(--text-primary))] truncate">
                        {doc.name}
                      </p>
                      <p className="text-2xs text-[hsl(var(--text-muted))]">
                        {doc.type} · Uploaded {doc.date} · Expires {doc.expires}
                      </p>
                    </div>
                    <span className="badge badge-draft text-2xs flex-shrink-0">{doc.sensitivity}</span>
                    <button
                      onClick={() => toast.info(`Viewing ${doc.name}`)}
                      className="btn-ghost p-1 flex-shrink-0"
                    >
                      <Eye size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Advance/Approve confirm dialog ───────────────────────────────── */}
        {showApproveConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl shadow-xl p-5 w-80 space-y-4">
              <div className="flex items-center gap-2">
                <ThumbsUp size={16} className="text-teal-600" />
                <h4 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Confirm Stage Advancement</h4>
              </div>
              <p className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">
                You are advancing <span className="font-semibold text-[hsl(var(--text-primary))]">{vendor.name}</span> from{' '}
                <span className="font-semibold text-teal-600">{currentStage}</span> to{' '}
                <span className="font-semibold text-teal-600">{nextStage}</span>. This action will be recorded in the audit trail.
              </p>
              {approvalComment && (
                <div className="p-2 rounded bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                  <p className="text-2xs text-[hsl(var(--text-muted))] italic">"{approvalComment}"</p>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowApproveConfirm(false)} className="btn-ghost text-xs">Cancel</button>
                <button onClick={confirmAdvance} className="btn-primary text-xs bg-teal-600 hover:bg-teal-700">
                  Confirm Advance
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Reject confirm dialog ────────────────────────────────────────── */}
        {showRejectConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl shadow-xl p-5 w-80 space-y-4">
              <div className="flex items-center gap-2">
                <ThumbsDown size={16} className="text-red-500" />
                <h4 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Confirm Rejection</h4>
              </div>
              <p className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">
                This will record a rejection for the current stage advancement of{' '}
                <span className="font-semibold text-[hsl(var(--text-primary))]">{vendor.name}</span>. The vendor will remain in the{' '}
                <span className="font-semibold text-teal-600">{currentStage}</span> stage.
              </p>
              {approvalComment && (
                <div className="p-2 rounded bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                  <p className="text-2xs text-[hsl(var(--text-muted))] italic">"{approvalComment}"</p>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowRejectConfirm(false)} className="btn-ghost text-xs">Cancel</button>
                <button onClick={confirmReject} className="px-3 py-1.5 rounded-md text-xs font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors">
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal footer */}
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex items-center justify-between flex-shrink-0 bg-[hsl(var(--background))]">
          <div className="flex items-center gap-3 text-2xs text-[hsl(var(--text-muted))]">
            <span>Last modified: 2026-03-28</span>
            <span>·</span>
            <span>Modified by: Kevin Tanaka</span>
          </div>
          <div className="flex items-center gap-2">
            {activeTab !== 'tab-workflow' && nextStage && (
              <button
                onClick={() => setActiveTab('tab-workflow')}
                className="flex items-center gap-1.5 btn-secondary text-xs"
              >
                <GitBranch size={12} />
                Manage Workflow
              </button>
            )}
            <button onClick={() => toast.info('Edit form coming soon.')} className="btn-secondary text-xs">
              Edit Vendor
            </button>
            <button onClick={onClose} className="btn-ghost text-xs">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}